import PropTypes from 'prop-types';
import { FunctionField, NumberField, ReferenceField, SelectField, TextField, usePermissions, useRecordContext, useTranslate } from 'react-admin';
import { Typography } from '@mui/material';
import { DetailsShow } from '@/components/global/drawers/DetailsShow.jsx';
import { DetailsActionsPopover } from '@/components/global/buttons/DetailsActionsPopover.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import { RelationLink } from '@/components/global/buttons/ListButtons.jsx';
import { ServiceCompanyNamesField } from '@/components/global/fields/ServiceCompanyNamesField.jsx';
import { ContractorLogoField } from '@/components/contractors/ContractorLogoField.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import { depositWithdrawalMethodsChoices, forwarderTypeChoices } from '@/components/global/options/selectOptions.jsx';
import NotificationDevicesIcon from '@/assets/icons/notification-devices-icon.svg?react';
import NotificationUsersIcon from '@/assets/icons/notification-users-icon.svg?react';
import GroupIcon from '@/assets/icons/group-icon.svg?react';

const ContractorActionButtons = ({ includeShow = true }) => {
    const record = useRecordContext();
    const { openDrawer } = useDrawer();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const canShow = includeShow && canViewDetails('contractor', permissions);
    const canEdit = Boolean(permissions?.isAdmin);

    if (!record) {
        return null;
    }

    return (
        <>
            {canShow && (
                <CustomShowModalButton
                    label={translate('resources.contractor.labels.showContractor')}
                    onOpen={() => openDrawer(record.id, 'contractor-show')}
                />
            )}
            {canEdit && (
                <CustomEditModalButton
                    label={translate('resources.contractor.labels.editContractor')}
                    onOpen={() => openDrawer(record.id, 'contractor-edit')}
                />
            )}
            <RelationLink
                label={translate('resources.contractor.labels.viewDevices')}
                startIcon={<NotificationDevicesIcon />}
                resource='device'
                filter='contractorId_in'
                sx={{ fontWeight: '400' }}
            />
            <RelationLink
                label={translate('resources.contractor.labels.viewDeviceGroups')}
                startIcon={<GroupIcon />}
                resource='device-group'
                filter='contractorId_in'
                sx={{ fontWeight: '400' }}
            />
            <RelationLink
                label={translate('resources.contractor.labels.viewUsers')}
                startIcon={<NotificationUsersIcon />}
                resource='user'
                filter='contractorIds_like'
                sx={{ fontWeight: '400' }}
            />
        </>
    );
};

ContractorActionButtons.propTypes = {
    includeShow: PropTypes.bool,
};

export const ContractorMoreActions = ({ variant = 'list' }) => {
    const record = useRecordContext();

    if (!record) {
        return null;
    }

    if (variant === 'details') {
        return (
            <DetailsActionsPopover>
                <ContractorActionButtons includeShow={false} />
            </DetailsActionsPopover>
        );
    }

    return (
        <CustomMorePopover record={record}>
            <ContractorActionButtons />
        </CustomMorePopover>
    );
};

ContractorMoreActions.propTypes = {
    variant: PropTypes.oneOf(['list', 'details']),
};

export const ContractorShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <DetailsShow
            handleClose={handleClose}
            elementId={elementId}
            resource='contractor'
            canView={permissions => canViewDetails('contractor', permissions)}
            actions={<ContractorMoreActions variant='details' />}
            showChangeHistory
            historyFieldsSource='resources.contractor.fields'
        >
            <ContractorLogoField source='currentLogo' />
            <TextField source='name' label={translate('resources.contractor.labels.contractorName')} />
            <TextField source='nip' label={translate('resources.contractor.fields.nip')} />
            <SelectField source='forwarderType' choices={forwarderTypeChoices(translate)} label={translate('resources.contractor.labels.forwarderType')} />
            <TextField source='code' label={translate('resources.contractor.labels.contractorCode')} />
            <FunctionField
                label={translate('resources.contractor.fields.operators')}
                render={record => <Typography variant='body2'>{(record?.operators || []).join(', ') || '—'}</Typography>}
            />
            <FunctionField
                label={translate('analytics.transaction.payoutForms')}
                render={record => {
                    const labels = (record?.depositWithdrawalMethods || [])
                        .map(id => depositWithdrawalMethodsChoices(translate).find(choice => choice.id === id)?.name)
                        .filter(Boolean);
                    return <Typography variant='body2'>{labels.length ? labels.join(', ') : '—'}</Typography>;
                }}
            />
            <TextField source='operatorId' label={translate('resources.contractor.labels.operatorId')} />
            <NumberField source='bottleDepositValue' label={translate('resources.contractor.labels.defaultBottleDepositValue')} />
            <NumberField source='loyaltyPointsPerBottle' label={translate('resources.contractor.labels.defaultLoyaltyPointsPerBottle')} />
            <NumberField source='discountLimitL1' label={translate('resources.contractor.labels.discountLimit', { limit: 1 })} />
            <NumberField source='discountLimitL2' label={translate('resources.contractor.labels.discountLimit', { limit: 2 })} />
            <NumberField source='discountLimitL3' label={translate('resources.contractor.labels.discountLimit', { limit: 3 })} />
            <NumberField source='discountLimitL4' label={translate('resources.contractor.labels.discountLimit', { limit: 4 })} />
            <NumberField source='discountLimitL5' label={translate('resources.contractor.labels.discountLimit', { limit: 5 })} />
            <NumberField source='discountLimitL6' label={translate('resources.contractor.labels.discountLimit', { limit: 6 })} />
            <NumberField source='discountValueL1' label={translate('resources.contractor.labels.discountValue', { value: 1 })} />
            <NumberField source='discountValueL2' label={translate('resources.contractor.labels.discountValue', { value: 2 })} />
            <NumberField source='discountValueL3' label={translate('resources.contractor.labels.discountValue', { value: 3 })} />
            <NumberField source='discountValueL4' label={translate('resources.contractor.labels.discountValue', { value: 4 })} />
            <NumberField source='discountValueL5' label={translate('resources.contractor.labels.discountValue', { value: 5 })} />
            <NumberField source='discountValueL6' label={translate('resources.contractor.labels.discountValue', { value: 6 })} />
            <ServiceCompanyNamesField source='serviceCompanyIds' label={translate('menu.serviceCompanies')} />
            <ReferenceField
                source='defaultServiceCompanyId'
                reference='service-company'
                link={false}
                label={translate('resources.contractor.labels.defaultServiceCompanyId')}
            >
                <TextField source='name' />
            </ReferenceField>
            <TextField source='description' label={translate('resources.contractor.labels.description')} />
        </DetailsShow>
    );
};

ContractorShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
